import { Card, Heading, Pagination, Text, type OptionProps } from '@amelio/ui-library';

import type { User } from '@/types/directory/User';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { EvaluationCard } from '@/components/EvaluationCard/EvaluationCard';
import { OngoingIllustration } from '@/components/Illustrations/Illustration';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { ReviewType } from '@/constants/review/CycleInstance';
import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';
import {
    getCycleInstanceEvaluationPeriod,
    getReviewTypeIcon,
    getReviewTypeLabel,
    hasRespondents,
} from '@/utils/cycleInstance';
import { formatPeriodRange } from '@/utils/periodRange';
import { getAllReviewers, getCurrentUser, getProgressPercentage } from '@/utils/reviewInstance';

const getReviewDate = (review: ReviewInstance): string | undefined => {
    return review.cycleInstance?.reviewType === ReviewType.MANAGER_EVALUATION
        ? (review.cycleInstance?.evaluationDate ?? undefined)
        : review.cycleInstance?.dueDate;
};

const PAGE_SIZE = 10;

interface OngoingReviewsProps {
    ongoingReviews: ReviewInstance[];
    isLoading: boolean;
    currentPage: number;
    totalCount: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    users: User[];
    actionMenuItems: (reviewInstance: ReviewInstance) => OptionProps[];
    onViewResults: (reviewInstance: ReviewInstance) => void;
    hasActiveFilters?: boolean;
}

export const OngoingReviews = ({
    ongoingReviews,
    isLoading,
    currentPage,
    totalCount,
    totalPages,
    onPageChange,
    users,
    actionMenuItems,
    onViewResults,
    hasActiveFilters,
}: OngoingReviewsProps) => {
    const { t } = useAmelioTranslations();

    // Only when there is nothing to show: swapping a populated list for bones collapses the
    // card height mid-fetch, and the browser clamps the page scroll.
    const showSkeleton = isLoading && ongoingReviews.length === 0;

    return (
        <Card
            className={
                'gradient-border-primary mt-40 px-4! py-4! sm:mt-0 sm:px-6! sm:py-6! md:-mt-40 md:px-8! md:py-6! lg:px-15! lg:pt-8! lg:pb-12!'
            }
            padding={'none'}
            variant={'flat'}
            borderRadius={'xl'}
        >
            <OngoingIllustration className={'relative -top-24 left-[65%] h-32 w-32 sm:left-4/5!'} />

            <div className={'-mt-24 mb-6 md:w-1/2'}>
                <Heading
                    level={3}
                    className={'text-heading-sm!'}
                >
                    {t('evaluation360Dashboard.ongoingReviews.title')}
                </Heading>

                <Text className={'mt-2'}>
                    {t('evaluation360Dashboard.ongoingReviews.description')}
                </Text>
            </div>

            {showSkeleton ? (
                <Skeleton className={'my-6'}>
                    <Bone className={'h-24 rounded-2xl'} />

                    <Bone className={'my-4 h-24 rounded-2xl'} />

                    <Bone className={'h-24 rounded-2xl'} />
                </Skeleton>
            ) : (
                <>
                    {ongoingReviews.length === 0 && hasActiveFilters && (
                        <Text className={'py-6 text-center'}>
                            {t('evaluation360Dashboard.noFilterResults')}
                        </Text>
                    )}

                    {ongoingReviews.length > 0 &&
                        ongoingReviews.map((review) => {
                            const reviewDate = getReviewDate(review);
                            // The period configured on the cycle, never derived from the evaluation
                            // date: a group-launched manager assessment has no date until the
                            // manager opens it, but it does carry the period picked at configuration.
                            const evaluationPeriod = getCycleInstanceEvaluationPeriod(
                                review.cycleInstance,
                            );
                            const evaluationPeriodLabel = evaluationPeriod
                                ? formatPeriodRange({
                                      from: evaluationPeriod.start,
                                      to: evaluationPeriod.end,
                                      language: i18n.language,
                                  })
                                : t('evaluation360Dashboard.ongoingReviews.noEvaluationPeriod');

                            return (
                                <EvaluationCard
                                    className={'my-2'}
                                    key={review.id}
                                    date={reviewDate}
                                    infoBadges={[
                                        {
                                            icon: getReviewTypeIcon(
                                                review?.cycleInstance?.reviewType ??
                                                    ReviewType.EVALUATION_360,
                                            ),
                                            id: `${review.id}-evaluation-type`,
                                            primaryLabel: getReviewTypeLabel(
                                                review.cycleInstance?.reviewType ??
                                                    ReviewType.EVALUATION_360,
                                            ),
                                            secondaryLabel: t('common.evaluationType'),
                                        },
                                        {
                                            id: `${review.id}-evaluation-period`,
                                            primaryLabel: evaluationPeriodLabel,
                                            secondaryLabel: t(
                                                'cycleEvaluationCycleInformation.period.label',
                                            ),
                                        },
                                    ]}
                                    progress={getProgressPercentage(review)}
                                    reviewers={
                                        hasRespondents(review.cycleInstance?.reviewType)
                                            ? getAllReviewers({ reviewInstance: review, users })
                                            : undefined
                                    }
                                    user={getCurrentUser({ reviewInstance: review, users })}
                                    variant={'Ongoing360'}
                                    actionMenuItems={actionMenuItems(review)}
                                    onCardClick={() => onViewResults(review)}
                                />
                            );
                        })}
                </>
            )}

            <Pagination
                variant={'rounded'}
                showResultsSummary={true}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={onPageChange}
                isLoading={isLoading}
                dataTestId={'ongoing-reviews-pagination'}
                resultsSummaryText={t('common.showingResults', {
                    start: totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
                    end: Math.min(currentPage * PAGE_SIZE, totalCount),
                    total: totalCount,
                })}
            />
        </Card>
    );
};
